import { useMemo } from 'react';
import {
  FormConfig,
  FormItem,
  FormPassthrough,
} from 'stories/forms/FormBuilder/FormBuilder.types';
import { InternalUserForm } from './types';
import { UserRole } from '@act/shared/definitions';
import { TIMEZONE_OPTIONS } from '@act/shared/constants';
import { Button, LoadingPage } from '@act/storybook';
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import {
  Controller,
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { CheckboxWrapper, FieldWrapper } from './Styles';
import { Refresh } from '@mui/icons-material';
import { TwilioIncomingPhoneNumberInstance } from '@act/shared/data-transfer-objects';

interface UseUserFormConfigProps {
  isEditing: boolean;
  userRoleString: string;
  isAssignableRole: boolean;
  shouldCreateAvonUser: boolean;
  hasAvonUser: boolean;
  availablePhoneNumbers: TwilioIncomingPhoneNumberInstance[] | null;
  availablePhoneNumbersLoading: boolean;
  showAvailableNumbers: boolean;
  getNumbers: () => void;
  getFormValue: UseFormGetValues<InternalUserForm>;
  setFormValue: UseFormSetValue<InternalUserForm>;
  setCurrentResourceNumber: (value: string | null) => void;
  setShowAvailableNumbers: (value: boolean) => void;
  setShouldCreateAvonUser: (value: boolean) => void;
  actingUserRole: UserRole;
  control: Control<InternalUserForm>;
}

export const useUserFormConfig = ({
  isEditing,
  userRoleString,
  isAssignableRole,
  shouldCreateAvonUser,
  hasAvonUser,
  availablePhoneNumbers,
  availablePhoneNumbersLoading,
  showAvailableNumbers,
  getNumbers,
  getFormValue,
  setFormValue,
  setCurrentResourceNumber,
  setShowAvailableNumbers,
  setShouldCreateAvonUser,
  actingUserRole,
  control,
}: UseUserFormConfigProps): FormConfig<InternalUserForm> => {
  const userRole = userRoleString ? (userRoleString as UserRole) : undefined;

  const isGuideOrSupervisor =
    userRole && [UserRole.GUIDE, UserRole.GUIDE_SUPERVISOR].includes(userRole);

  const roleConfig: (FormItem<InternalUserForm> | FormPassthrough)[] =
    isAssignableRole
      ? [
          {
            type: 'input',
            name: 'role',
            label: 'User role',
            input: 'select',
            props: {
              options: [
                UserRole.GUIDE,
                UserRole.PROVIDER,
                UserRole.GUIDE_SUPERVISOR,
              ].map(role => ({
                key: role,
                label: role.replace('_', ' '),
                value: role,
              })),
              fullWidth: true,
            },
          } as FormItem<InternalUserForm>,
        ]
      : [
          {
            type: 'passthrough',
            content: (
              <FieldWrapper>
                <Typography variant="titleMedium" sx={{ padding: '8px 0' }}>
                  Role: {userRole}
                </Typography>
              </FieldWrapper>
            ),
          } as FormPassthrough,
        ];

  return useMemo(() => {
    return [
      {
        type: 'label',
        label: 'User Details',
      },
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'firstName',
            label: 'First Name',
            input: 'text',
          },
          {
            type: 'input',
            name: 'lastName',
            label: 'Last Name',
            input: 'text',
          },
        ],
      },
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'email',
            label: 'Reema Email',
            input: 'text',
            disabled: isEditing,
          },
          {
            type: 'input',
            name: 'mobilePhone',
            label: 'Mobile Phone',
            input: 'phone',
          },
        ],
      },
      {
        type: 'label',
        label: 'Role Details',
      },

      {
        type: 'group',
        items: [
          ...roleConfig,
          {
            type: 'passthrough',
            content: (
              <CheckboxWrapper>
                <Checkbox
                  checked={shouldCreateAvonUser || hasAvonUser}
                  onClick={() => setShouldCreateAvonUser(!shouldCreateAvonUser)}
                  disabled={hasAvonUser}
                />
                {hasAvonUser ? (
                  <Typography variant="bodyLarge">
                    User has Avon account
                  </Typography>
                ) : (
                  <Typography variant="bodyLarge">
                    Provision Avon User
                  </Typography>
                )}
              </CheckboxWrapper>
            ),
          },
        ],
      },

      {
        type: 'group',
        items: [
          ...(!isEditing && isGuideOrSupervisor && !!getFormValue('role')
            ? [
                {
                  type: 'input',
                  name: 'resourcingNumberBase',
                  label: 'Resourcing Number (Guides and Supervisors ONLY)',
                  input: 'number',
                  props: {
                    characterLimit: 10,
                  },
                } as FormItem<InternalUserForm>,
                {
                  type: 'passthrough',
                  content:
                    !showAvailableNumbers && !getFormValue('resourceNumber') ? (
                      <Button onClick={getNumbers}>
                        Get Available Phone Numbers
                      </Button>
                    ) : null,
                } as FormPassthrough,
              ]
            : []),
          ...(availablePhoneNumbersLoading
            ? [
                {
                  type: 'passthrough',
                  content: <LoadingPage />,
                } as FormPassthrough,
              ]
            : []),
          ...(availablePhoneNumbers &&
          availablePhoneNumbers.length > 0 &&
          showAvailableNumbers
            ? [
                {
                  type: 'passthrough',
                  content: (
                    <List>
                      <Typography variant="titleSmall">
                        Please select a number
                      </Typography>
                      {availablePhoneNumbers.map(number => (
                        <ListItem
                          sx={{
                            '&:hover': {
                              cursor: 'pointer',
                              backgroundColor: 'lightgray',
                            },
                          }}
                          key={number.phoneNumber}
                          onClick={() => {
                            setFormValue('resourceNumber', number.phoneNumber);
                            setCurrentResourceNumber(number.phoneNumber);
                            setShowAvailableNumbers(false);
                          }}
                        >
                          <ListItemText primary={number.phoneNumber} />
                        </ListItem>
                      ))}
                    </List>
                  ),
                } as FormPassthrough,
              ]
            : []),
          ...(availablePhoneNumbers &&
          availablePhoneNumbers.length === 0 &&
          showAvailableNumbers
            ? [
                {
                  type: 'passthrough',
                  content: (
                    <Typography variant="titleSmall" sx={{ color: 'red' }}>
                      No available phone numbers with that resourcing number
                    </Typography>
                  ),
                } as FormPassthrough,
              ]
            : []),
          ...(!!getFormValue('resourceNumber')
            ? [
                {
                  type: 'input',
                  name: 'resourceNumber',
                  label: 'Resource Number',
                  input: 'text',
                  // Ensure this field is controlled by the form and can be cleared if needed
                  // Also, consider if it should be disabled if a number is selected.
                } as FormItem<InternalUserForm>,
              ]
            : []),
          ...(availablePhoneNumbers &&
          availablePhoneNumbers.length > 0 &&
          showAvailableNumbers &&
          !availablePhoneNumbersLoading
            ? [
                {
                  type: 'passthrough',
                  content: (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        startIcon={<Refresh />}
                        sx={{ justifyContent: 'flex-start' }}
                        onClick={getNumbers}
                      >
                        Refresh
                      </Button>
                    </Box>
                  ),
                } as FormPassthrough,
              ]
            : []),
        ],
      },
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'timeZone',
            label: 'Timezone',
            input: 'select',
            props: {
              options: TIMEZONE_OPTIONS.map(option => ({
                key: option,
                label: option.replace('_', ' '),
                value: option,
              })),
            },
          },
          {
            type: 'passthrough',
            content: (
              <CheckboxWrapper>
                <Controller
                  name="hasAppAccess"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Checkbox
                        checked={!!field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        disabled={
                          actingUserRole &&
                          actingUserRole !== UserRole.SUPER_ADMIN
                        }
                      />
                      <Typography variant="bodyLarge">
                        Has app access
                      </Typography>
                    </>
                  )}
                />
              </CheckboxWrapper>
            ),
          } as FormPassthrough,
        ],
      },
    ];
  }, [
    isEditing,
    userRole,
    isAssignableRole,
    shouldCreateAvonUser,
    hasAvonUser,
    availablePhoneNumbers,
    availablePhoneNumbersLoading,
    showAvailableNumbers,
    getNumbers,
    getFormValue,
    setFormValue,
    setCurrentResourceNumber,
    setShouldCreateAvonUser,
  ]);
};
